import { omit } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { tracker } from "@utils/openreplay";

import {
  SectionSubtitle,
  PrescriptionDetailsContainer,
  Footer,
  SectionDisclaimer,
  EmptyRequest,
  ContentContainer,
  AddMedicineButton,
  Content,
  HeaderContainer,
  PatientContent,
  PatientSummaryAccordionContainer,
  PrescriptionContent,
  RouterLink,
  SaveAsTemplateLabel,
  Title,
} from "./styles/PrescriptionCreate.styles";
import {
  Body,
  BottomActionArea,
  BottomActionButton,
  Header,
  PatientSummaryAccordion,
  NavigateBeforeButton,
  FullScreenLoader,
  DoctorDocumentReadinessModal,
  PatientRequestCommentAccordion,
  DigitalSign,
  PatientSummary,
  Checkbox,
} from "@components";
import type { RequestDetail } from "@interfaces";
import {
  patientDefaultValues,
  RequestStatus,
  RequestType,
  TemplateType,
} from "@interfaces";
import {
  queryClient,
  useDocumentCreationContext,
  useDocumentSignatureContext,
  useMedicineSearchContext,
} from "@contexts";
import {
  useCreateTemplate,
  useDeletePrescriptionDetail,
  useRequestData,
  usePersistentCounter,
  useRequestSigningStatus,
  useDoctorDocumentReadiness,
} from "@hooks";
import { removeFromLocalStorage, saveToLocalStorage } from "@utils";
import { useDocumentNotificationContext } from "@contexts/DocumentNotificationContext";
import { scrollToTop } from "@components/ScrollToTop";
import { InteractionAlertCard } from "./components/InteractionAlertCard";
import {
  clearAllInteractionsStores,
  useInteractionsStore,
} from "@hooks/useInteractionsStore";
import { TrialProChip } from "@components/Pro/TrialProChip";
import type { Theme } from "@mui/material";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { StyledInfoIconSecondary } from "@styles/styled";
import { PrescriptionDetailCard } from "./components/PrescriptionDetailCard";
import { SaveAsTemplateModal } from "./components/SaveAsTemplateModal";

interface PrescriptionCreateLocationState {
  detailAdded?: boolean;
  medicineType?: string;
  fromTemplate?: boolean;
}

export function PrescriptionCreate() {
  const location = useLocation();

  const {
    detailAdded = false,
    medicineType = "",
    fromTemplate = false,
  } = (location.state as PrescriptionCreateLocationState) || {};

  const [isDetailAdded, setIsDetailAdded] = useState<boolean>(detailAdded);
  const [showDigitalSign, setShowDigitalSign] = useState<boolean>(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const isProcessingSimplePrescriptionRef = useRef<boolean>(false);

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { requestId = "" } = useParams();

  const { ensureReadyToIssue, stopIfDeadlinePassed, modalProps } =
    useDoctorDocumentReadiness({
      requestId,
      stopBlockedOnOpen: true,
    });

  const {
    data,
    isPending: isPendingRequest,
    isFetching: isFetchingRequest,
    refetch: refetchRequest,
  } = useRequestData();

  const { setPinState } = useDocumentSignatureContext();
  const { setSearchText } = useMedicineSearchContext();
  const { interactions, deleteDrug } = useInteractionsStore(requestId);

  const { isSigning, isSigningOrError, isInmutable, isModifiable } =
    useRequestSigningStatus(data?.request.status);

  const { triggerCreatePrescription, isCreatingPrescription } =
    useDocumentCreationContext();

  const { notifyPrescription } = useDocumentNotificationContext();

  const [, , resetUnknownErrorRetries] = usePersistentCounter(
    "unknownSignatureErrorRetries",
  );

  const { mutateAsync: createTemplate, isPending: isSavingTemplate } =
    useCreateTemplate();

  const { mutate: deleteDetail } = useDeletePrescriptionDetail({
    requestId,
    request: data?.request,
    onSuccess: () => setIsDetailAdded(false),
  });

  const {
    requestDetails,
    heldDetails,
    simpleDetails,
    patient,
    isDraft,
    onlySimpleDetails,
  } = useMemo(() => {
    const requestDetails = data?.details || [];

    const heldDetails = requestDetails.filter(
      (detail) => detail.prescriptionType === "HELD",
    );

    const simpleDetails = requestDetails.filter(
      (detail) => detail.prescriptionType !== "HELD",
    );

    return {
      requestDetails,
      heldDetails,
      simpleDetails,
      patient: data?.patient ?? patientDefaultValues,
      isDraft: data?.request.status === RequestStatus.DRAFT,
      onlySimpleDetails: heldDetails.length === 0 && simpleDetails.length > 0,
    };
  }, [data]);

  const handleBack = useCallback(() => {
    if (isSigning) {
      navigate("/app/solicitudes");
      enqueueSnackbar("La receta ya está en proceso de firma", {
        variant: "info",
      });

      return;
    }

    clearAllInteractionsStores();
    if (isDraft) {
      navigate(`/app/nuevo-documento/paciente`, {
        state: { documentType: RequestType.PRESCRIPTION },
      });
    } else {
      navigate(`/app/solicitudes/${requestId}/gestionar/seleccionar-documento`);
    }

    removeFromLocalStorage("requestType");
  }, [navigate, isDraft, requestId, isSigning, enqueueSnackbar]);

  const onDigitalSignSubmit = useCallback(async () => {
    // Set the ref FIRST to prevent useEffect from firing
    isProcessingSimplePrescriptionRef.current = true;
    setShowDigitalSign(false);
    if (!data?.request) {
      isProcessingSimplePrescriptionRef.current = false;

      return;
    }

    try {
      // The backend handles both creation and signing in one step in FES
      await triggerCreatePrescription({
        request: data.request,
        requestDetailData: requestDetails,
        signType: "FES",
      });

      // Invalidate queries and navigate immediately
      // Notification will run in the background (fire and forget)
      removeFromLocalStorage("requestType");
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["request", requestId] });

      // Start notification in background, don't wait for it
      notifyPrescription({ requestId }).catch((error) => {
        console.error("Error notifying prescription:", error);
        // Do not throw the error, it will be handled at another level
      });

      navigate(`/app/solicitudes/${requestId}/receta/exito`);
    } catch (error) {
      console.error("Error in onDigitalSignSubmit:", error);
      isProcessingSimplePrescriptionRef.current = false;
      if (stopIfDeadlinePassed(error)) return;
      enqueueSnackbar("Error al generar la receta. Por favor, inténtalo de nuevo.", {
        variant: "error",
      });
    }
  }, [
    data,
    requestDetails,
    requestId,
    triggerCreatePrescription,
    enqueueSnackbar,
    stopIfDeadlinePassed,
    navigate,
    notifyPrescription,
  ]);

  const onDigitalSignSubmitFEA = useCallback(() => {
    setShowDigitalSign(false);
    if (!data) return;

    // Create the prescription with signType: "FEA" in the background
    // Don't await - let it run while user enters PIN and OTP
    triggerCreatePrescription({
      request: data.request,
      requestDetailData: requestDetails,
      signType: "FEA",
    }).catch((error) => {
      console.error("Error creating prescription:", error);
      // Error will be handled by DocumentCreationContext
    });

    saveToLocalStorage({
      key: "requestType",
      value: RequestType.PRESCRIPTION,
    });
    setPinState({
      pin: "",
      error: false,
      errorMessage: "",
    });
    navigate(`/app/solicitudes/${requestId}/receta/firma`);
  }, [data, requestDetails, requestId, triggerCreatePrescription, navigate, setPinState]);

  const proceedToSign = useCallback(() => {
    saveToLocalStorage({
      key: "requestType",
      value: RequestType.PRESCRIPTION,
    });

    const showSignature = () => {
      setShowDigitalSign(true);
      scrollToTop();
    };

    ensureReadyToIssue(showSignature);
  }, [ensureReadyToIssue]);

  const handleSubmit = useCallback(() => {
    if (saveAsTemplate) {
      setShowTemplateModal(true);

      return;
    }

    proceedToSign();
  }, [saveAsTemplate, proceedToSign]);

  const handleSaveTemplate = useCallback(
    async (templateName: string) => {
      try {
        await createTemplate({
          name: templateName,
          templateType: TemplateType.PRESCRIPTION,
          prescriptionDetails: requestDetails.map((detail) =>
            omit(detail, ["id", "requestId"]),
          ),
        });
        await queryClient.invalidateQueries({ queryKey: ["templates"] });
        enqueueSnackbar("Plantilla guardada exitosamente", {
          variant: "success",
        });
      } catch (error) {
        console.error("Error saving template:", error);
        enqueueSnackbar("Error al guardar la plantilla", { variant: "error" });
      }

      setShowTemplateModal(false);
      proceedToSign();
    },
    [createTemplate, requestDetails, enqueueSnackbar, proceedToSign],
  );

  const handleDelete = useCallback(
    (detail: RequestDetail) => {
      if (!data?.request) return;
      deleteDetail(detail);
      deleteDrug(detail.activePrinciple ?? "", data.details ?? []);
    },
    [deleteDetail, deleteDrug, data],
  );

  const handleEdit = useCallback(
    (detail: RequestDetail) => {
      tracker?.event("UpdatePrescriptionDetail", {
        source: "PrescriptionCreateHandleEdit",
        detail,
      });
      navigate(`/app/solicitudes/${requestId}/receta/editar/tratamiento/${detail.id}`);
    },
    [navigate, requestId],
  );

  useEffect(() => {
    refetchRequest();
  }, [refetchRequest]);

  useEffect(() => {
    // Don't trigger this effect while processing simple prescription
    // Use a function to check the ref value at execution time
    if (isProcessingSimplePrescriptionRef.current) {
      return;
    }

    // Only navigate if we're not processing simple prescriptions
    if (isSigningOrError || isInmutable) {
      // Double check the ref before navigating
      if (isProcessingSimplePrescriptionRef.current) {
        return;
      }

      navigate("/app/solicitudes");

      const snackbarMessage = isSigningOrError
        ? "La receta ya está en proceso de firma"
        : "La receta no está disponible para firmar";

      enqueueSnackbar(snackbarMessage, {
        variant: isSigningOrError ? "info" : "error",
      });
    }
  }, [navigate, isSigningOrError, isInmutable, enqueueSnackbar]);

  // Reset ref when component unmounts (after navigation)
  useEffect(() => {
    return () => {
      isProcessingSimplePrescriptionRef.current = false;
    };
  }, []);

  useEffect(() => {
    const clearSearchText = () => {
      setSearchText("");
    };

    clearSearchText();
  }, [setSearchText]);

  // Reset unknown error retries when creating a new prescription
  useEffect(() => {
    resetUnknownErrorRetries();
  }, [resetUnknownErrorRetries]);

  const renderPrescriptionDetailCardSkeleton = (quantity: number) =>
    Array.from({ length: quantity }).map((_, i) => (
      <PrescriptionDetailCard.Skeleton key={i} />
    ));

  if (isPendingRequest) {
    return (
      <>
        <Header
          title={isMobile ? "Resumen receta" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Cargando información..." />
          </Body>
        </ContentContainer>
      </>
    );
  }

  const isFetchingDetails = isDetailAdded && isFetchingRequest;

  if (isCreatingPrescription) {
    return (
      <>
        <Header
          title={isMobile ? "Resumen receta" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Generando receta" />
          </Body>
        </ContentContainer>
      </>
    );
  }

  if (showDigitalSign) {
    return (
      <DigitalSign
        documentType={RequestType.PRESCRIPTION}
        handleCreateDocument={
          onlySimpleDetails ? onDigitalSignSubmit : onDigitalSignSubmitFEA
        }
        handleBack={() => setShowDigitalSign(false)}
      />
    );
  }

  return (
    <>
      <Header
        title={isMobile ? "Resumen receta" : undefined}
        useMekiDocIcon={!isMobile}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <ContentContainer disableGutters>
        <HeaderContainer>
          <Title variant="h4" fontWeight={"medium"}>
            Resumen receta
          </Title>
          <RouterLink to={"buscar"}>
            <AddMedicineButton
              variant="outlined"
              disabled={isFetchingDetails || isSigning}
              size="large"
            >
              <Typography variant="body1" fontWeight={"medium"}>
                Agregar medicamento
              </Typography>
            </AddMedicineButton>
          </RouterLink>
        </HeaderContainer>

        <Content>
          <PatientContent>
            {!isMobile && (
              <PatientSummary
                patient={patient}
                prevRoute={`/app/solicitudes/${requestId}/receta/crear`}
                requestId={requestId}
                canEdit={isDraft}
              />
            )}
            <PatientSummaryAccordionContainer>
              <PatientSummaryAccordion
                patient={patient}
                prevRoute={`/app/solicitudes/${requestId}/receta/crear`}
                requestId={requestId}
                isOpen={isDraft && !(detailAdded || requestDetails.length > 0)}
                canEdit={isDraft}
              />
              <PatientRequestCommentAccordion
                request={data?.request}
                canReject={false}
                isExpanded={true}
              />
            </PatientSummaryAccordionContainer>
          </PatientContent>
          <PrescriptionContent>
            {/* Skeleton for the first detail added */}
            {isFetchingDetails &&
              heldDetails.length === 0 &&
              simpleDetails.length === 0 && (
                <>
                  <SectionSubtitle>
                    {medicineType === "HELD" ? "Receta retenida" : "Receta simple"}
                  </SectionSubtitle>
                  <PrescriptionDetailsContainer>
                    <PrescriptionDetailCard.Skeleton />
                  </PrescriptionDetailsContainer>
                </>
              )}
            {/* Skeleton for when a held detail already exists */}
            {data?.details && heldDetails.length > 0 && (
              <>
                <SectionSubtitle>Receta retenida</SectionSubtitle>
                <PrescriptionDetailsContainer>
                  {isFetchingDetails
                    ? renderPrescriptionDetailCardSkeleton(
                        medicineType === "HELD"
                          ? heldDetails.length + 1
                          : heldDetails.length,
                      )
                    : heldDetails.map((detail) => {
                        return (
                          <PrescriptionDetailCard
                            key={detail.id}
                            detail={detail}
                            handleEdit={
                              isModifiable ? () => handleEdit(detail) : undefined
                            }
                            handleDelete={
                              isModifiable ? () => handleDelete(detail) : undefined
                            }
                          />
                        );
                      })}
                </PrescriptionDetailsContainer>
                {isFetchingDetails &&
                  simpleDetails.length === 0 &&
                  medicineType === "SIMPLE" && (
                    <>
                      <SectionSubtitle>Receta simple</SectionSubtitle>
                      <PrescriptionDetailCard.Skeleton />
                    </>
                  )}
              </>
            )}
            {/* Skeleton for when a simple detail already exists */}
            {data?.request && simpleDetails.length > 0 && (
              <>
                {isFetchingDetails &&
                  heldDetails.length === 0 &&
                  medicineType === "HELD" && (
                    <>
                      <SectionSubtitle>Receta retenida</SectionSubtitle>
                      <PrescriptionDetailCard.Skeleton />
                    </>
                  )}
                <SectionSubtitle>Receta simple</SectionSubtitle>
                <PrescriptionDetailsContainer>
                  {isFetchingDetails
                    ? renderPrescriptionDetailCardSkeleton(
                        medicineType === "SIMPLE"
                          ? simpleDetails.length + 1
                          : simpleDetails.length,
                      )
                    : simpleDetails.map((detail) => {
                        return (
                          <PrescriptionDetailCard
                            key={detail.id}
                            detail={detail}
                            handleEdit={
                              isModifiable ? () => handleEdit(detail) : undefined
                            }
                            handleDelete={
                              isModifiable ? () => handleDelete(detail) : undefined
                            }
                          />
                        );
                      })}
                </PrescriptionDetailsContainer>
              </>
            )}
            {requestDetails.length > 0 && !fromTemplate && (
              <Box display="flex" alignItems="center" gap="8px">
                <Checkbox
                  checked={saveAsTemplate}
                  onChange={(e) => setSaveAsTemplate(e.target.checked)}
                  label={
                    <SaveAsTemplateLabel>Guardar como plantilla</SaveAsTemplateLabel>
                  }
                />
              </Box>
            )}
            {interactions.length > 0 && (
              <>
                <SectionSubtitle style={{ paddingBottom: "8px" }}>
                  Posibles interacciones
                  <TrialProChip />
                </SectionSubtitle>
                <SectionDisclaimer>
                  Interacciones mayores según fuentes especializadas. Referencial y no
                  exhaustiva.
                </SectionDisclaimer>
                <InteractionAlertCard />
              </>
            )}
            {requestDetails.length > 0 ? (
              <Footer>
                <BottomActionArea style={{ backgroundColor: "inherit", border: "none" }}>
                  <BottomActionButton
                    style={{ width: "100%" }}
                    onClick={handleSubmit}
                    disabled={isFetchingDetails || isSigning}
                  >
                    Generar receta
                  </BottomActionButton>
                </BottomActionArea>
              </Footer>
            ) : (
              <EmptyRequest>
                <StyledInfoIconSecondary />
                No tienes medicamentos agregados
              </EmptyRequest>
            )}
          </PrescriptionContent>
        </Content>

        <DoctorDocumentReadinessModal {...modalProps} />
        <SaveAsTemplateModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          onSave={handleSaveTemplate}
          loading={isSavingTemplate}
        />
      </ContentContainer>
    </>
  );
}
